import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { buildIssueCategoryMap } from '../../../constants/issueTypes';
import {
  buildIssueReportContext,
  buildIssueReportEnvelopeIdentity,
  ISSUE_REPORT_HITL_BUILD,
} from '../../../utils/issueReportContext';
import {
  buildIssueReportEnvelope,
  configureIssueReportTransport,
  deliverIssueReportEnvelope,
} from '../../../utils/issueReportTransport';
import {
  IssueReportEventData,
  IssueReportRecord,
  IssueReportSource,
  IssueReportState,
} from './issueReport.props';

/**
 * HITL-1492 Pillar 3, Part A (Capture).
 *
 * Two invariants this slice exists to hold:
 *
 * 1. `context` is captured once on press and never re-read. The task-router teardown
 *    wipes the session state a later classification would need.
 * 2. Records SURVIVE session teardown. Nothing here reacts to resetAI,
 *    resetSelectedRestaurantDetails, or the restaurant/stage/menu-version resets that
 *    sessionBoundary listens for — HITL-1501's unclassified chip and the pre-lobby
 *    panel both need reports to outlive the session. The only reset is the existing
 *    `user/logout` wipe in rootReducer, which is shift-end and correct.
 */

/**
 * Growth bound in place of a session reset. The event is already sent by the time a
 * record would be evicted, so eviction only loses the Undo/amend affordance on an
 * ancient record — never a report.
 */
export const MAX_RETAINED_REPORTS = 50;

/**
 * How long the flag panel stays open — for Undo AND for classifying. ONE deadline,
 * rendered once, so the two can never disagree.
 *
 * 10s rather than 5s because the panel is now the ONLY classification surface: an
 * agent who does not classify here never can. It is still safe to be short —
 * timing out costs a type, never a report.
 */
export const FLAG_WINDOW_MS = 10000;

/**
 * Absolute ceiling on the window, measured from `created_at` and enforced REGARDLESS
 * of pause state.
 *
 * Required, not a nicety: focusing the detail field pauses the countdown, so a
 * focused-and-abandoned field would otherwise hold the panel open forever. This is
 * what guarantees every flag reaches a terminal state.
 */
export const MAX_FLAG_WINDOW_MS = 60000;

/**
 * Cap on `issue_detail`. Matches TRANSCRIPT_LINE_CHAR_CAP — this is the one field a
 * human types into under time pressure, so it is also the most likely place a
 * customer name or card fragment enters the payload, and an unbounded paste would
 * blow the 48 KB keepalive budget on the pagehide path.
 */
export const ISSUE_DETAIL_CHAR_CAP = 500;

/** Trim, cap, and normalise blank to null so `''` never reaches the warehouse. */
export const sanitizeIssueDetail = (
  detail: string | null | undefined
): string | null => {
  if (typeof detail !== 'string') return null;
  const trimmed = detail.trim();
  return trimmed ? trimmed.slice(0, ISSUE_DETAIL_CHAR_CAP) : null;
};

export const initialState: IssueReportState = {
  byId: {},
  order: [],
  activeReportId: null,
  activeWindowExpiresAt: null,
  remainingMs: null,
};

const evictOldest = (state: IssueReportState) => {
  while (state.order.length > MAX_RETAINED_REPORTS) {
    const oldest = state.order.shift();
    if (oldest) {
      delete state.byId[oldest];
      if (state.activeReportId === oldest) {
        state.activeReportId = null;
        state.activeWindowExpiresAt = null;
      }
    }
  }
};

/**
 * Build the wire payload for a record at its CURRENT revision.
 *
 * `context` rides along on revision 0 and on any later revision whose revision 0 was
 * never acknowledged. Without that second condition a lost rev 0 plus a landed rev 1
 * would leave a classified report with no session join at all — no session_id, no
 * store_code — which is precisely the failure this pillar exists to eliminate. It
 * costs nothing because the context is already frozen on the record, so it survives
 * the task-router teardown that makes the amend context-free in the first place.
 */
export const buildIssueReportEventData = (
  record: IssueReportRecord
): IssueReportEventData => {
  const includeContext = record.revision === 0 || !record.rev0Delivered;
  return {
    schema_version: '1.0',
    hitl_build: ISSUE_REPORT_HITL_BUILD,
    issue_id: record.issue_id,
    revision: record.revision,
    voided: record.voided,
    source: record.source,
    // Derived here rather than stored on the record: one source of truth
    // (ISSUE_TYPES) and no chance of a record carrying a stale pairing.
    issue_category: record.issue_type
      ? buildIssueCategoryMap()[record.issue_type] ?? null
      : null,
    issue_type: record.issue_type,
    issue_detail: record.issue_detail,
    context: includeContext ? record.context : null,
  };
};

const issueReportSlice = createSlice({
  name: 'issueReport',
  initialState,
  reducers: {
    reportCreated: (state, action: PayloadAction<IssueReportRecord>) => {
      const record = action.payload;
      state.byId[record.issue_id] = record;
      state.order.push(record.issue_id);
      state.activeReportId = record.issue_id;
      state.activeWindowExpiresAt =
        Date.parse(record.created_at) + FLAG_WINDOW_MS;
      state.remainingMs = null;
      evictOldest(state);
    },
    reportAmended: (
      state,
      action: PayloadAction<{
        issue_id: string;
        issue_type: string | null;
        issue_detail: string | null;
      }>
    ) => {
      const { issue_id, issue_type, issue_detail } = action.payload;
      const record = state.byId[issue_id];
      if (!record) return;
      record.revision += 1;
      record.issue_type = issue_type;
      record.issue_detail = sanitizeIssueDetail(issue_detail);
      record.status = 'pending';
    },
    reportVoided: (state, action: PayloadAction<{ issue_id: string }>) => {
      const record = state.byId[action.payload.issue_id];
      if (!record) return;
      // Idempotent (HITL-1499): a double-click on the toast's Undo inside one
      // frame would otherwise bump the revision twice and emit TWO retractions,
      // at revisions 1 and 2. One retraction per report, whatever the agent does
      // to the button.
      if (record.voided) return;
      record.revision += 1;
      record.voided = true;
      record.status = 'pending';
    },
    sendSucceeded: (
      state,
      action: PayloadAction<{ issue_id: string; revision: number }>
    ) => {
      const { issue_id, revision } = action.payload;
      const record = state.byId[issue_id];
      if (!record) return;
      if (revision === 0) {
        record.rev0Delivered = true;
      }
      // Only the newest revision governs the record's status; a late ack for an older
      // revision must not mark a freshly amended record as fully sent.
      if (revision >= record.revision) {
        record.status = 'sent';
      }
    },
    sendFailed: (
      state,
      action: PayloadAction<{ issue_id: string; revision: number }>
    ) => {
      const { issue_id, revision } = action.payload;
      const record = state.byId[issue_id];
      if (!record) return;
      // Same staleness guard as sendSucceeded above: a send is in flight per
      // revision, so a failure for an OLDER revision must not mark a record that has
      // since been amended. Without this, a slow rev-0 failure lands on a rev-1
      // record the agent has already re-classified and reports it as failed.
      if (revision < record.revision) return;
      record.status = 'failed';
    },
    closeFlagWindow: (state) => {
      state.activeReportId = null;
      state.activeWindowExpiresAt = null;
      state.remainingMs = null;
    },
    /**
     * Stop the countdown while the agent types a detail.
     *
     * PAUSE, not extend. Extending on each keystroke has no defined exit — an agent
     * pulled into an order mid-sentence would leave a panel that never closes — and a
     * per-keystroke reset is a second clock racing the first.
     *
     * `expiresAt` is an absolute timestamp, so pausing cannot just stall an interval:
     * the remainder is banked here and `expiresAt` is nulled. The two are MUTUALLY
     * EXCLUSIVE — a paused window always has `expiresAt === null`.
     *
     * MAX_FLAG_WINDOW_MS still applies; see useIssueFlagWindow.
     */
    pauseFlagWindow: (state) => {
      if (!state.activeReportId || state.activeWindowExpiresAt === null) return;
      state.remainingMs = Math.max(0, state.activeWindowExpiresAt - Date.now());
      state.activeWindowExpiresAt = null;
    },
    /** Resume from the BANKED remainder, never from a fresh FLAG_WINDOW_MS. */
    resumeFlagWindow: (state) => {
      if (!state.activeReportId || state.remainingMs === null) return;
      state.activeWindowExpiresAt = Date.now() + state.remainingMs;
      state.remainingMs = null;
    },
  },
});

export const {
  reportCreated,
  reportAmended,
  reportVoided,
  sendSucceeded,
  sendFailed,
  closeFlagWindow,
  pauseFlagWindow,
  resumeFlagWindow,
} = issueReportSlice.actions;

/**
 * Wire the transport and hand it the current record so it can be delivered.
 *
 * ONE emission per revision, over REST. There is no websocket mirror: it duplicated
 * every rev-0 report onto a second envelope whose only shared key was
 * `(issue_id, revision)`, and — because the two seq counters both start at 0 and the
 * websocket one advanced on rev 0 only — the first report of a tab shipped TWO
 * `issue_report` events both carrying `seq: 0`. It bought no reliability either:
 * `socketMiddleware` drops silently when the socket is closed, which is the same
 * window the REST send fails in, and REST is the path with retries and a pagehide
 * flush. Envelope `id` is the dedupe key here, as it is for every other event type.
 */
const deliver = createAsyncThunk(
  'issueReport/deliver',
  async ({ issueId }: { issueId: string }, { getState, dispatch }) => {
    const state = getState();
    const record = state.issueReport.byId[issueId];
    if (!record) return;

    configureIssueReportTransport(
      {
        eventAPI: state.config.EVENT_API,
        eventAPIAuth: state.config.EVENT_API_BASIC_AUTH_PARAM,
      },
      {
        onSuccess: (issue_id, revision) =>
          dispatch(sendSucceeded({ issue_id, revision })),
        onFailure: (issue_id, revision) =>
          dispatch(sendFailed({ issue_id, revision })),
      }
    );

    const data = buildIssueReportEventData(record);
    const { email, username, id, firstName, lastName } =
      state.user?.userProfile || ({} as any);

    deliverIssueReportEnvelope(
      buildIssueReportEnvelope({
        data,
        // Frozen at press time, NOT read live: by the time an amend is submitted at
        // the task-router boundary these have been wiped from Redux.
        sessionId: record.envelopeIdentity.session_id,
        restaurantCode: record.envelopeIdentity.restaurant_code,
        laneId: record.envelopeIdentity.lane_id,
        agentMetadata: {
          agent_name: username,
          agent_id: id,
          agent_email: email,
          agent_first_name: firstName,
          agent_last_name: lastName,
        },
      })
    );
  }
);

/**
 * Flag an issue. O(1) synchronous work on the press path — a uuid and one pure
 * context build — and NO network on this call stack (NFR-1).
 *
 * ONE OPEN REPORT AT A TIME. A press while a flag window is open is dropped, and
 * that guard lives HERE rather than on the button on purpose: the button is one of
 * three entry points. `Alt+I` is another and HITL-1504's `transcript_turn` will be a
 * third, so a guard in the component is a guard the other two walk straight past.
 *
 * The cost of not having it is not a duplicate row. `reportCreated` points the flag
 * window at the NEWEST report, so a second press silently swapped the panel over and
 * the first report lost its only chance to be classified — permanently, because
 * `Unclassified` is terminal. The agent got no indication that had happened.
 *
 * Scoped to the WINDOW, not the session: once the panel closes the next press files
 * normally. A session legitimately has several separate problems, and the count in
 * the button is what tells the agent how many they have already filed.
 *
 * `issue_type` / `issue_detail` ride on REVISION 0 when the caller already knows
 * them — the transcript gestures do, because clicking a word IS the classification.
 * Filing unclassified and amending afterwards would lose the type outright whenever
 * the agent is pulled into the order in between, since `Unclassified` is terminal.
 * The action-bar press passes neither and still files unclassified.
 */
export const flagIssue = createAsyncThunk(
  'issueReport/flagIssue',
  async (
    {
      source = 'action_bar' as IssueReportSource,
      issue_type = null,
      issue_detail = null,
    }: {
      source?: IssueReportSource;
      issue_type?: string | null;
      issue_detail?: string | null;
    },
    { getState, dispatch }
  ) => {
    const state = getState();
    // `activeReportId` IS "a flag window is open" — `closeFlagWindow` and
    // `voidIssue` both null it, and the hard cap guarantees one of them runs.
    if (state.issueReport.activeReportId) return null;

    const issueId = uuidv4();

    const record: IssueReportRecord = {
      issue_id: issueId,
      envelopeIdentity: buildIssueReportEnvelopeIdentity(state),
      revision: 0,
      status: 'pending',
      voided: false,
      rev0Delivered: false,
      source,
      created_at: new Date().toISOString(),
      issue_type,
      issue_detail: sanitizeIssueDetail(issue_detail),
      context: buildIssueReportContext(state),
    };

    dispatch(reportCreated(record));
    dispatch(deliver({ issueId }));
    return issueId;
  }
);

/**
 * Classify a report. One thunk for BOTH classification paths — HITL-1500's tray sets
 * the type alone, HITL-1501's panel also passes `issue_detail`. Two thunks would
 * drift on revision handling.
 */
export const commitIssueType = createAsyncThunk(
  'issueReport/commitIssueType',
  async (
    {
      issue_id,
      issue_type,
      issue_detail = null,
    }: {
      issue_id: string;
      issue_type: string | null;
      issue_detail?: string | null;
    },
    { dispatch }
  ) => {
    dispatch(reportAmended({ issue_id, issue_type, issue_detail }));
    // Closing the window is the CALLER's job, not this thunk's: the panel closes on
    // a chip click, but also commits on timeout — where the window is already gone.
    dispatch(deliver({ issueId: issue_id }));
  }
);

/**
 * Undo. Emits a RETRACTION at the next revision — it does not cancel the revision-0
 * send, because committing on press is the whole point: a refresh, a car exit or a
 * network blip inside the flag window would otherwise lose the flag, and blips are
 * exactly what agents are flagging.
 */
export const voidIssue = createAsyncThunk(
  'issueReport/voidIssue',
  async ({ issue_id }: { issue_id: string }, { getState, dispatch }) => {
    // Guarded here as well as in the reducer: the reducer guard keeps the
    // revision at 1, but without this a second click would still SEND revision 1
    // a second time. Consumers dedupe on (issue_id, revision), so that is
    // survivable — it is just a wasted request on the press path.
    const record = getState().issueReport.byId[issue_id];
    if (!record || record.voided) return;

    dispatch(reportVoided({ issue_id }));
    dispatch(closeFlagWindow());
    dispatch(deliver({ issueId: issue_id }));
  }
);

export default issueReportSlice.reducer;
